/** Modalidades em que normalmente não há data de leilão/praça. */
const TIPOS_VENDA_SEM_DATA_LEILAO = new Set([
  'Venda Direta Online',
  'Venda Online',
  'Licitação Aberta',
]);

const SAO_PAULO_TZ = 'America/Sao_Paulo';

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function midnightSaoPauloLocal(y: number, month: number, day: number): Date {
  return new Date(`${y}-${pad2(month)}-${pad2(day)}T00:00:00-03:00`);
}

function calendarPartsInSaoPaulo(instant: Date): { y: number; m: number; d: number } | null {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: SAO_PAULO_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(instant);
  const y = Number(parts.find((p) => p.type === 'year')?.value);
  const m = Number(parts.find((p) => p.type === 'month')?.value);
  const d = Number(parts.find((p) => p.type === 'day')?.value);
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
  return { y, m, d };
}

/** Sempre dd/mm/aaaa — não depende do locale do SO/navegador. */
export function formatCalendarPtBR(y: number, month: number, day: number): string {
  return `${pad2(day)}/${pad2(month)}/${y}`;
}

function formatInstantDatePtBR(instant: Date): string {
  const p = calendarPartsInSaoPaulo(instant);
  if (!p) return '';
  return formatCalendarPtBR(p.y, p.m, p.d);
}

function dayStartFromInstantInSaoPaulo(instant: Date): Date {
  const p = calendarPartsInSaoPaulo(instant);
  if (!p) return instant;
  return midnightSaoPauloLocal(p.y, p.m, p.d);
}

function formatInstantTimePtBR(instant: Date): string {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: SAO_PAULO_TZ,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(instant);
  const hh = parts.find((p) => p.type === 'hour')?.value ?? '00';
  const mm = parts.find((p) => p.type === 'minute')?.value ?? '00';
  return `${hh}:${mm}`;
}

/** Resolve dd/mm vs mm/dd em strings com barras (padrão BR: dd/mm). */
function resolveSlashDateParts(a: number, b: number): { day: number; month: number } | null {
  if (a > 12 && b >= 1 && b <= 12) return { day: a, month: b };
  if (b > 12 && a >= 1 && a <= 12) return { day: b, month: a };
  if (a >= 1 && a <= 12 && b >= 1 && b <= 12) return { day: a, month: b };
  return null;
}

export type ParsedAuctionDateTime = {
  instant: Date;
  dayStartSaoPaulo: Date;
  hasTime: boolean;
};

/**
 * Aceita `03/06/2026`, `03/06/2026 10:00`, `03/06/2026, 08:00:00` (toLocaleString pt-BR),
 * `03/06/2026 - 10:00`, `03/06/2026 às 10:00`.
 */
function parseSlashDateTime(s: string): ParsedAuctionDateTime | null {
  const m = s.match(
    /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:,?\s+(?:[-–]|às|as)?\s*(\d{1,2}):(\d{2})(?::(\d{2}))?)?\s*$/i
  );
  if (!m) return null;

  const a = Number(m[1]);
  const b = Number(m[2]);
  const y = Number(m[3]);
  const resolved = resolveSlashDateParts(a, b);
  if (!resolved) return null;
  const { day: da, month: mo } = resolved;
  if (mo < 1 || mo > 12 || da < 1 || da > 31) return null;

  const dayStart = midnightSaoPauloLocal(y, mo, da);
  if (m[4] != null && m[5] != null) {
    const hh = Number(m[4]);
    const mm = Number(m[5]);
    if (!Number.isFinite(hh) || !Number.isFinite(mm)) return null;
    return {
      instant: new Date(`${y}-${pad2(mo)}-${pad2(da)}T${pad2(hh)}:${pad2(mm)}:00-03:00`),
      dayStartSaoPaulo: dayStart,
      hasTime: true,
    };
  }
  return { instant: dayStart, dayStartSaoPaulo: dayStart, hasTime: false };
}

/**
 * Interpreta textos de data do BE/scraper sempre no contexto de São Paulo.
 * Evita `Date.parse('03/06/2026 10:00')` (vira mm/dd nos engines US).
 */
export function parseAuctionDateTimeRaw(
  raw: string | null | undefined
): ParsedAuctionDateTime | null {
  if (raw == null) return null;
  const s = raw.trim();
  if (!s) return null;

  const slashParsed = parseSlashDateTime(s);
  if (slashParsed) return slashParsed;

  if (/\d{1,2}\/\d{1,2}\/\d{2,4}/.test(s)) return null;

  const isoDateOnly = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoDateOnly) {
    const y = Number(isoDateOnly[1]);
    const mo = Number(isoDateOnly[2]);
    const da = Number(isoDateOnly[3]);
    if (mo < 1 || mo > 12 || da < 1 || da > 31) return null;
    const dayStart = midnightSaoPauloLocal(y, mo, da);
    return { instant: dayStart, dayStartSaoPaulo: dayStart, hasTime: false };
  }

  if (/^\d{4}-\d{2}-\d{2}T/.test(s)) {
    const t = Date.parse(s);
    if (Number.isNaN(t)) return null;
    const instant = new Date(t);
    const dayStart = dayStartFromInstantInSaoPaulo(instant);
    const hasTime = !/T00:00:00(\.000)?(Z|[+-]\d{2}:\d{2})?$/.test(s);
    return { instant, dayStartSaoPaulo: dayStart, hasTime };
  }

  const t = Date.parse(s);
  if (!Number.isNaN(t)) {
    const instant = new Date(t);
    return {
      instant,
      dayStartSaoPaulo: dayStartFromInstantInSaoPaulo(instant),
      hasTime: /\d:\d{2}/.test(s),
    };
  }

  return null;
}

export function parseAuctionDateRaw(raw: string | null | undefined): Date | null {
  return parseAuctionDateTimeRaw(raw)?.dayStartSaoPaulo ?? null;
}

export function startOfTodaySaoPaulo(ref = new Date()): Date {
  return dayStartFromInstantInSaoPaulo(ref);
}

export function formatAuctionDateTimeBR(raw: string | null | undefined): string {
  const parsed = parseAuctionDateTimeRaw(raw);
  if (!parsed) return '';
  const date = formatInstantDatePtBR(parsed.dayStartSaoPaulo);
  if (!parsed.hasTime) return date;
  return `${date} às ${formatInstantTimePtBR(parsed.instant)}`;
}

export function formatAuctionDateBR(raw: string | null | undefined): string {
  const parsed = parseAuctionDateTimeRaw(raw);
  if (!parsed) return '';
  return formatInstantDatePtBR(parsed.dayStartSaoPaulo);
}

export type AuctionCountdown = {
  days: number;
  activeRaw: string;
  activeDateBR: string;
  activeDateTimeBR: string;
};

export function resolveAuctionCountdown(input: {
  firstRaw?: string | null;
  secondRaw?: string | null;
  tipoVenda?: string | null;
}): AuctionCountdown | null {
  const firstRaw = input.firstRaw?.trim() || null;
  const secondRaw = input.secondRaw?.trim() || null;
  const first = parseAuctionDateRaw(firstRaw);
  const second = parseAuctionDateRaw(secondRaw);
  const today = startOfTodaySaoPaulo();

  if (!first && !second) {
    if (input.tipoVenda && TIPOS_VENDA_SEM_DATA_LEILAO.has(input.tipoVenda)) return null;
    return null;
  }

  let activeDay: Date | null = null;
  let activeRaw: string | null = null;

  if (first && first.getTime() >= today.getTime()) {
    activeDay = first;
    activeRaw = firstRaw;
  } else if (second && second.getTime() >= today.getTime()) {
    activeDay = second;
    activeRaw = secondRaw;
  } else {
    return null;
  }

  const days = Math.max(0, Math.round((activeDay.getTime() - today.getTime()) / 86_400_000));
  const parsedActive = parseAuctionDateTimeRaw(activeRaw);
  const activeDateBR = formatInstantDatePtBR(activeDay);
  const activeDateTimeBR = parsedActive?.hasTime
    ? `${activeDateBR} às ${formatInstantTimePtBR(parsedActive.instant)}`
    : activeDateBR;

  return {
    days,
    activeRaw: activeRaw!,
    activeDateBR,
    activeDateTimeBR,
  };
}

export function formatLeilaoEmCell(input: {
  diasRestantes: number | null | undefined;
  dataLeilaoAtiva?: string | null;
}): string {
  if (input.diasRestantes == null) return '—';
  const d = input.diasRestantes;
  const datePart = input.dataLeilaoAtiva?.trim();
  if (d <= 10) return datePart ? `⚡ ${d}d — ${datePart}` : `⚡ ${d}d`;
  return datePart ? `${d}d — ${datePart}` : `${d}d`;
}

export function leilaoEmUrgencyClass(diasRestantes: number | null | undefined): string {
  if (diasRestantes == null) return 'text-muted-foreground';
  if (diasRestantes <= 10) return 'text-danger';
  if (diasRestantes <= 20) return 'text-warning';
  return 'text-muted-foreground';
}
